import { createField } from '@/components/forms/field.schema';
import { CascadingProjectReference } from '@/components/forms/fields/CascadingProjectReference';
import { TicketStatusField } from '@/components/forms/fields/TicketStatusField';
import { TicketTitleField } from '@/components/forms/fields/TicketTitleField';
import { Ticket } from '@certhon/models';
import {
  Bell,
  Briefcase,
  Building,
  Calendar,
  FileText,
  Hash,
  Link,
  PenLine,
  Tag,
  User,
  Users,
} from 'lucide-react';
import { z } from 'zod';

/**
 * Resolution context for the virtual `project`/`customer` foreignKey fields
 * below. The owning table (e.g. TicketTable) is expected to provide these
 * lookup maps; their identity must change when the underlying queries
 * resolve so that columns/tokens rebuild and FK cells re-render.
 */
interface TicketFieldContext {
  workToProject?: Record<number, number>;
  projectToCustomer?: Record<number, number>;
}

function resolveTicketProjectId(
  t: Ticket,
  ctx?: TicketFieldContext,
): number | null {
  if (t.project_id) return t.project_id;
  if (!t.werk_id) return null;
  return ctx?.workToProject?.[t.werk_id] ?? null;
}

function resolveTicketCustomerId(
  t: Ticket,
  ctx?: TicketFieldContext,
): number | null {
  const pid = resolveTicketProjectId(t, ctx);
  if (!pid) return null;
  return ctx?.projectToCustomer?.[pid] ?? null;
}

/**
 * Ensures all fields of EntityType are present as keys in the target type,
 * unless explicitly excluded via ExcludedKeys.
 *
 * // FIXME: use 'satisfies' to properly  check model compliance
 */
export type RequireAllFields<
  EntityType,
  ExcludedKeys extends keyof EntityType = never,
> = {
  [K in Exclude<keyof EntityType, ExcludedKeys>]-?: unknown;
};

export const ticketFields = {
  number: createField(z.number(), {
    fieldType: 'text',
    label: 'Number',
    readOnly: true,
    icon: Hash,
  }),
  title: createField(z.string().min(5, 'Title must be at least 5 characters'), {
    component: TicketTitleField,
    fieldType: 'text',
    fullWidth: true,
    label: 'Title',
    icon: PenLine,
    placeholder: 'A short, descriptive title',
  }),
  project_id: createField(z.int().nullable(), {
    component: CascadingProjectReference,
    fullWidth: true,
    visibleIn: ['edit'],
    label: 'Linked to',
    icon: Link,
    fieldType: 'custom', // Will be overridden by component
    constraints: {
      allowCustomer: false, // Tickets can't reference customers directly
      allowProject: true,
      allowWork: true,
    },
  }),
  werk_id: createField(z.int().nullable(), {
    label: 'Work',
    fieldType: 'foreignKey',
    entity: 'work',
    icon: Briefcase,
  }),
  // Virtual FK: ticket has no direct project FK — resolved via t.project_id
  // or via t.werk_id → work.project_id. Reads from the works query cache.
  project: createField(z.int().nullable(), {
    label: 'Project',
    fieldType: 'foreignKey',
    entity: 'project',
    icon: Building,
    getVirtualValue: (t: Ticket, ctx?: TicketFieldContext) =>
      resolveTicketProjectId(t, ctx),
  }),
  // Virtual FK: customer is derived from the resolved project's klant_id.
  // Reads from the projects query cache.
  customer: createField(z.int().nullable(), {
    label: 'Customer',
    fieldType: 'foreignKey',
    entity: 'customer',
    icon: Users,
    getVirtualValue: (t: Ticket, ctx?: TicketFieldContext) =>
      resolveTicketCustomerId(t, ctx),
  }),
  // FIXME: having some issues with zod and changeMeta.
  hidden_werk_id: createField(z.int().nullable(), {
    label: 'Work',
    fieldType: 'hidden',
    entity: 'work',
  }),
  // Third leg of the CRM chain; declared so it is visible to
  // LayoutToFormValues. CascadingProjectReference writes it.
  hidden_client_id: createField(z.int().nullable(), {
    label: 'Customer',
    fieldType: 'hidden',
    entity: 'customer',
  }),
  status: createField(z.enum(['opened', 'closed']), {
    component: TicketStatusField,
    fieldType: 'select', // implicit because enum
    label: 'Status',
  }),
  description: createField(z.string().optional(), {
    fullWidth: true,
    label: 'Description',
    icon: FileText,
    fieldType: 'richtext',
    placeholder: 'Provide a detailed description of the issue...',
  }),
  due: createField(z.date().nullable(), {
    fieldType: 'date',
    label: 'Due Date',
    icon: Calendar,
  }),
  assignee_user_id: createField(z.number().nullable(), {
    fieldType: 'foreignKey',
    entity: 'user',
    label: 'Assignee',
    icon: User,
  }),
  executor_user_id: createField(z.number().nullable(), {
    fieldType: 'foreignKey',
    entity: 'user',
    label: 'Executor',
    icon: User,
  }),
  labels: createField(z.array(z.number()), {
    fieldType: 'foreignKeyArray',
    entity: 'label',
    label: 'Labels',
    icon: Tag,
  }),
  creator_user_id: createField(z.number(), {
    fieldType: 'foreignKey',
    entity: 'user',
    label: 'Creator',
    readOnly: true,
    icon: User,
  }),
  created: createField(z.date(), {
    fieldType: 'date',
    label: 'Created',
    readOnly: true,
    icon: Calendar,
  }),
  modified: createField(z.date(), {
    fieldType: 'date',
    label: 'Modified',
    readOnly: true,
    icon: Calendar,
  }),
  notification_opt_in: createField(z.array(z.number()), {
    fieldType: 'foreignKeyArray',
    entity: 'user',
    label: 'Notified',
    readOnly: true,
    icon: Bell,
  }),
};
